/* eslint-disable react/prop-types */
// Internal section wrappers: each receives the engine `{ item, state, form }`
// (shape documented in the JSDoc below); prop-types validation is unnecessary
// for these thin closures.
import React from 'react';
import _ from 'lodash';
import RedeemOfferVulcan from '../vulcanHelpers/couponCode/RedeemOfferVulcan';
import BusinessOptikTvTable from '../vulcanHelpers/businessOptikTv';
import OfficePhoneLine from '../vulcanHelpers/officePhone';
import FibreInternet from '../vulcanHelpers/fibreInternet';
import CopperInternet from '../vulcanHelpers/copperInternet';
import TsbConsolidation from '../vulcanHelpers/consolidationTSB';
import { removeCouponsNotForTsbPlan } from '../vulcanHelpers/couponCodeOfferForInternets/utils';
import UserDetailsForm from '../userDetailsForm';
import EquipmentSectionForm from '../vulcanHelpers/tsbSection/equipmentSectionFormVulcan';
import RepeatedForm from '../repeatedForm';
import { withSectionBoundaries } from './SectionErrorBoundary';
import { doesPostalCodeMatch } from './postalCodeMatch';
import { isVulcanItemHidden } from './vulcanItemHides';
import { buildInitialEquipmentBlob } from './seedEquipmentGroupSection';
import { buildFormFieldsView } from './formFieldsView';

import {
  deriveDeclarativeVisibility,
  VulcanSectionChromeContext
} from './vulcanStructuralComponents';
import {
  fieldAddProductVulcan,
  secureBusinessPlanVulcan,
  tsbTerm,
  fieldIsVulcanOrder,
  fibreEastRestrictedProvinces,
  productCatalog
} from '../../utils/constant';

/**
 * Vulcan's bespoke inline sections for `<FormRunner>`'s `sectionRegistry`, keyed
 * by field `type` (the engine dispatches a leaf field whose type is registered
 * here to the section — engine alpha.2). Each of these six sections owns ONE
 * ancillary blob in the engine's one state bag (`form.values[KEY]`), written via
 * `form.setValue(KEY, …)` — replacing the six separate `useState`s in
 * formStepVulcan.
 *
 * The existing section COMPONENTS are reused unchanged (like the field registry);
 * these are thin wrappers mapping the engine `form` + a closed-over app context
 * (`orderData`, the `setIsFormTouched` / `setShowDownloadQuoteFooter` chrome
 * callbacks, and locale copy `t`) onto each component's existing props. The app
 * context is supplied by `createVulcanSectionRegistry` at the Stage-3 wiring
 * point, since those handlers live in formStepVulcan's component scope.
 *
 * Errors: each section reads `form.errors[KEY]` gated by touched / revealErrors —
 * bespoke validation is wired at Stage 3, so these are typically undefined until
 * then.
 *
 * @typedef {Object} VulcanSectionAppContext
 * @property {Object} orderData
 * @property {(touched: boolean) => void} [setIsFormTouched]
 * @property {(show: boolean) => void} [setShowDownloadQuoteFooter]
 * @property {Object} t   Active-locale `vulcanWebform` copy
 * @property {string} [isEditing]     Order-edit flag (formStepVulcan scope)
 * @property {boolean} [isFieldReset]
 * @property {string} [channelName]        Equipment: sales channel (pricing/UI)
 * @property {boolean} [postalCodeMatches] Equipment: install-postal gating
 */

// Stable empty fallback — passing a fresh `{}` as a prop each render re-fires any
// child effect that depends on it (RedeemOfferVulcan's `useEffect(…, [orderData])`
// then setStates a fresh `[]`, looping the live step). A frozen singleton keeps
// the prop identity stable across renders.
const EMPTY_OBJECT = Object.freeze({});

const errorFor = (form, key) => {
  const err = form.errors && form.errors[key];
  if (!err) return undefined;
  return form.revealErrors || (form.touched && form.touched[key])
    ? err
    : undefined;
};

/** Read the scalar a `formFields` bag entry carries (leaf) or the blob itself. */
const entryValue = entry =>
  entry && typeof entry === 'object' && 'value' in entry ? entry.value : entry;
const isLeafEntry = entry =>
  entry && typeof entry === 'object' && 'value' in entry;

/**
 * Bridge the app section's `setFormFields(fullBag)` write contract onto the
 * engine: persist every key the component actually changed via `form.setValue`.
 * Because the component funnels ALL its mutations — its own fields, cross-field
 * writes (the fibreSpeed reset on province change), and nested sub-bag writes
 * (equipment's `setObjectValue`) — through this one setter, they all land in the
 * engine with no per-write extraction. Handles both shapes:
 *   - a leaf entry (`'value' in entry`) → push the scalar `entry.value`;
 *   - a nested blob / other object → deep-compare and push the whole object.
 *
 * Unchanged keys are skipped, which is LOAD-BEARING: `form.setValue` always
 * builds a new `values` object, so writing an identical value still changes its
 * identity, which would re-run any section effect keyed on the derived
 * `formFields` (equipment's `useEffect(…, [formFields])`) — an infinite loop.
 *
 * `getForm`/`getPending` are GETTERS, not captured values. The app's real
 * `setFormFields` is a plain setState that REPLACES the whole bag, so when two
 * effects write in the same commit the second overwrites the first
 * (last-write-wins). Diffing per key against a stale render snapshot broke that:
 * UserDetailsForm's mount effect clears its fields to '' (populateFormFields'
 * `else` branch), then its edit-mode effect re-writes them from `orderData` —
 * but against the pre-clear snapshot that re-write looked like a no-op and was
 * skipped, leaving the fields blank (confirmed real-data bug, order 83480 step 1:
 * Signing Authority empty under ?fce=1). `pending` records what this batch has
 * already queued so the comparison reflects the latest intent, restoring
 * last-write-wins; the caller resets it whenever the engine's `values` identity
 * advances (i.e. the batch committed).
 */
const makeSetFormFields = (getForm, getPending) => next => {
  const form = getForm();
  const pending = getPending();
  Object.keys(next || {}).forEach(k => {
    const entry = next[k];
    const nextVal = entryValue(entry);
    const current = pending.has(k) ? pending.get(k) : form.values[k];
    const changed = isLeafEntry(entry)
      ? nextVal !== current
      : !_.isEqual(nextVal, current);
    if (changed) {
      pending.set(k, nextVal);
      form.setValue(k, nextVal);
    }
  });
};

/**
 * Per-section hook wiring the `formFields` view + `setFormFields` bridge to the
 * LIVE engine. Refs (not captured props) so a write issued from an effect sees
 * the latest engine state and this batch's already-queued writes; `pending` is
 * dropped as soon as the engine's `values` identity advances.
 */
function useFormFieldsBridge(form) {
  const formRef = React.useRef(form);
  const pendingRef = React.useRef(null);
  if (pendingRef.current === null || formRef.current.values !== form.values) {
    pendingRef.current = new Map();
  }
  formRef.current = form;

  const formFields = React.useMemo(() => buildFormFieldsView(form), [
    form.values,
    form.fieldStates
  ]);
  const setFormFields = React.useMemo(
    () =>
      makeSetFormFields(
        () => formRef.current,
        () => pendingRef.current
      ),
    []
  );
  return { formFields, setFormFields };
}

/**
 * Shape the engine's flat `errors` map ({name: message}) into the
 * `{name: {text}}` shape UserDetailsForm reads, gated by touched/revealErrors.
 *
 * `editedKeys` (optional Set of field names the user genuinely edited) replaces
 * the engine's `touched` in that gate. A section that populates its own fields
 * from effects (UserDetailsForm) can't use `touched`: every such write goes
 * through `form.setValue`, which marks the field touched, so its required-field
 * errors would show on load.
 */
const buildFormErrorsView = (form, editedKeys) => {
  const view = {};
  Object.keys(form.errors || {}).forEach(k => {
    const touched = editedKeys
      ? editedKeys.has(k)
      : form.touched && form.touched[k];
    const shown = form.revealErrors || touched;
    if (form.errors[k] && shown) view[k] = { text: form.errors[k] };
  });
  return view;
};

/**
 * @param {VulcanSectionAppContext} appCtx
 * @param {Object} [components]  Optional component overrides (DI for tests); defaults to the real sections.
 * @returns {Object.<string, import('react').ComponentType<{item: Object, state: Object, form: Object}>>}
 */
export function createVulcanSectionRegistry(appCtx, components = {}) {
  const {
    // FALLBACK ONLY — see `useLiveOrderData`. VulcanFormStepRunner deliberately
    // no longer passes this (it would make the registry rebuild on every save);
    // it remains for callers that render sections without a chrome provider,
    // e.g. the bare `<FormRunner>` harnesses in the migration tests.
    orderData: fallbackOrderData = {},
    setIsFormTouched,
    setShowDownloadQuoteFooter,
    t = {},
    isEditing = '',
    isFieldReset = false,
    channelName = null,
    postalCodeMatches = false
  } = appCtx || {};

  /**
   * The LIVE `orderData`.
   *
   * It must NOT be captured at factory time: `orderData` changes identity on
   * every save, so making it a registry dependency rebuilt the registry —
   * which creates brand-new section component FUNCTIONS and therefore remounts
   * every section, wiping their state (the invariant documented on
   * VulcanFormStepRunner's `sectionRegistry` memo).
   *
   * Read from `chrome.visibilityContext`, which is re-provided every render —
   * the same live channel `isFormTouched`/`multiSelectFieldNames`/
   * `backendFieldValidationMode` already use. Reading it during RENDER (rather
   * than freezing it) is load-bearing: `UserDetailsForm`
   * (`useEffect(…, [isEditing, orderData])`) and `RepeatedForm` re-populate
   * ONLY when the `orderData` prop identity changes, so a frozen value would
   * trade the remount bug for a silent stale-data one.
   */
  function useLiveOrderData() {
    const chrome = React.useContext(VulcanSectionChromeContext) || {};
    const live = chrome.visibilityContext && chrome.visibilityContext.orderData;
    return live || fallbackOrderData;
  }
  const Coupon = components.RedeemOfferVulcan || RedeemOfferVulcan;
  const OptikTv = components.BusinessOptikTvTable || BusinessOptikTvTable;
  const OfficePhone = components.OfficePhoneLine || OfficePhoneLine;
  const Fibre = components.FibreInternet || FibreInternet;
  const Copper = components.CopperInternet || CopperInternet;
  const Tsb = components.TsbConsolidation || TsbConsolidation;
  const UserDetails = components.UserDetailsForm || UserDetailsForm;
  const Equipment = components.EquipmentSectionForm || EquipmentSectionForm;
  const Repeated = components.RepeatedForm || RepeatedForm;
  const touch = () => {
    if (setIsFormTouched) setIsFormTouched(true);
    if (setShowDownloadQuoteFooter) setShowDownloadQuoteFooter(false);
  };

  function CouponSection({ form }) {
    // `orderData` is RedeemOfferVulcan's ONE-TIME initializer (its
    // `useEffect(…, [orderData])` restores the saved `redeemOfferSelection`).
    // It must NOT be the live `form.values.redeemOfferData` — the component WRITES
    // that via onChange, so echoing it back as the input creates a push/pull
    // feedback loop. Capture the initial (saved) value ONCE so the input identity
    // is stable; onChange keeps writing the live value to the engine bag for
    // pricing/payload.
    const initialOrderData = React.useRef(
      form.values.redeemOfferData || EMPTY_OBJECT
    ).current;
    return (
      <Coupon
        onChange={v => {
          form.setValue('redeemOfferData', v);
          touch();
        }}
        orderData={initialOrderData}
        buttonError={errorFor(form, 'redeemOfferData')}
        setIsFormTouched={setIsFormTouched}
        setShowDownloadQuoteFooter={setShowDownloadQuoteFooter}
      />
    );
  }

  function OptikTvSection({ item, form }) {
    // The ancillary-blob key the PRICING path reads
    // (`engine.values.businessOptikTvTableData` — formStepVulcan.js's
    // vulcanPricing/getFormData calls, and pricingAdapter.js's six-blob
    // convention).
    const key = 'businessOptikTvTableData';
    // The FIELD's own Contentful name — what BusinessOptikTvTable itself
    // emits (`{target: {name: fieldName, …}}`) and what `getFormData` reads
    // back for the payload (`fields[k].value` where `type === 'OptikTvTable'`),
    // which is why a saved order carries an `OptikTvTable`-named key.
    const fieldName = item.field ? item.field.name : undefined;
    // BusinessOptikTvTable requires `formFields` (PropTypes.shape({}).isRequired)
    // to read monthlyRatePlanOptikTvVul/termBusinessOptikTvVul/
    // profileTypeOptikTvVul/addProductVulcan's `.value` — it gates rendering the
    // whole table on `!selectedRatePlan || !selectedTerm` returning null. This
    // was never wired here (only `fields`, the raw Contentful def, was passed),
    // so `formFields` was always undefined and the table never rendered,
    // regardless of what the user selected. Same `useFormFieldsBridge` bridge
    // UserDetailsSection/etc. already use for this exact need.
    const { formFields } = useFormFieldsBridge(form);
    // Reveal-only (parity with legacy, which surfaced it on Continue): the
    // table writes itself on mount, so a `touched` gate would show it on load.
    const tableError =
      fieldName && form.revealErrors && form.errors
        ? form.errors[fieldName]
        : undefined;
    return (
      <OptikTv
        fieldName={fieldName}
        fields={item.field || item.raw}
        formFields={formFields}
        onChange={e => {
          if (e && e.target && e.target.type === 'OptikTvTable') {
            // Write BOTH: the field's own name so the table reaches the save
            // payload and round-trips through `orderData` like any other
            // field, and the ancillary key the pricing path reads. Writing
            // only the ancillary key (as this did) broke persistence in both
            // directions — the payload saved `''`, and `initialData` read a
            // key nothing ever seeds from `orderData`, so a saved table never
            // came back. Confirmed real-data bug: a saved order carried
            // `OptikTvTable: ''` while its plan/term/profile fields saved fine.
            if (fieldName) form.setValue(fieldName, e.target.value);
            form.setValue(key, e.target.value);
            touch();
          }
        }}
        error={tableError ? { text: tableError } : undefined}
        // Field value first (engine-seeded from `orderData`, so a saved table
        // restores for free); the ancillary key is the in-session fallback.
        initialData={(fieldName && form.values[fieldName]) || form.values[key]}
      />
    );
  }

  function OfficePhoneSection({ item, form }) {
    const key = 'officePhoneLinesData';
    // Same dual-write as OptikTvSection above, and for the same reason: the
    // field's own name is what `getFormData` reads back for the payload
    // (`dataOrder.addLineOfficePhoneVulcan`, read by vulcanOrderSummary.js),
    // while `officePhoneLinesData` is the ancillary key the pricing path
    // reads. Writing only the ancillary key left the summary's
    // `addLineOfficePhoneVulcan` empty even though lines were added.
    // Confirmed real-data bug: Office Phone lines priced correctly but never
    // appeared in the Order Summary.
    const fieldName = item.field ? item.field.name : undefined;
    return (
      <OfficePhone
        fieldName={fieldName}
        fields={item.field || item.raw}
        error={errorFor(form, key)}
        officePhoneLinesData={
          (fieldName && form.values[fieldName]) || form.values[key]
        }
        onOfficePhoneLinesChange={lines => {
          if (fieldName) form.setValue(fieldName, lines);
          form.setValue(key, lines);
          touch();
        }}
        setIsFormTouched={setIsFormTouched}
        setShowDownloadQuoteFooter={setShowDownloadQuoteFooter}
      />
    );
  }

  function FibreConsolidationSection({ form }) {
    const key = 'fibreInternetData';
    const orderData = useLiveOrderData();
    const products = form.values[fieldAddProductVulcan] || [];
    // isVulcanOrder answered "No" in a fibreEastRestrictedProvinces province
    // (Ontario only, as of 2026-07-20 — client request) means a WLN Fibre
    // East order — restrict the Add Offer modal to 1.5G + 3G speeds, same as
    // the Vulcan flow. isVulcanOrder stores the LOCALIZED value ("No" in EN,
    // "Non" in FR), so compare against the locale-aware `t.no`, not a
    // hard-coded "No" — otherwise the restriction never activates in French.
    // Case-insensitive province match for consistency with the rest of this
    // check's spirit (see constant.js's own comment on the list).
    const isFibreEastRestricted =
      fibreEastRestrictedProvinces
        .map(province => province.toLowerCase())
        .includes((orderData.province_VUL || '').toLowerCase()) &&
      orderData[fieldIsVulcanOrder] === t.no;
    return (
      <Fibre
        fibreInternetData={form.values[key] || productCatalog}
        setFibreInternetData={v => {
          form.setValue(key, v);
          touch();
        }}
        orderData={orderData}
        error={errorFor(form, key)}
        isInternetBackupSelected={products.includes(t.vulInternetBackup)}
        isFibreEastRestricted={isFibreEastRestricted}
        setIsFormTouched={setIsFormTouched}
        setShowDownloadQuoteFooter={setShowDownloadQuoteFooter}
      />
    );
  }

  function CopperConsolidationSection({ form }) {
    const key = 'copperInternetData';
    const orderData = useLiveOrderData();
    return (
      <Copper
        copperInternetData={form.values[key] || productCatalog}
        setCopperInternetData={v => {
          form.setValue(key, v);
          touch();
        }}
        orderData={orderData}
        error={errorFor(form, key)}
        setIsFormTouched={setIsFormTouched}
        setShowDownloadQuoteFooter={setShowDownloadQuoteFooter}
      />
    );
  }

  function TsbConsolidationSection({ form }) {
    const key = 'tsbData';
    const orderData = useLiveOrderData();
    const selectedPlan = form.values[secureBusinessPlanVulcan];
    const previousPlan = React.useRef(selectedPlan);
    React.useEffect(() => {
      if (previousPlan.current === selectedPlan) return;
      previousPlan.current = selectedPlan;
      const pruned = removeCouponsNotForTsbPlan(form.values[key], selectedPlan);
      if (pruned !== form.values[key]) {
        form.setValue(key, pruned);
        touch();
      }
    }, [selectedPlan]);
    return (
      <Tsb
        tsbData={form.values[key]}
        setTsbData={v => {
          form.setValue(key, v);
          touch();
        }}
        orderData={orderData}
        tsbTerm={form.values[tsbTerm]}
        isVulcanFlow={orderData.isThisVulcanOrder}
        selectedTsbPlan={form.values[secureBusinessPlanVulcan]}
        setIsFormTouched={setIsFormTouched}
        setShowDownloadQuoteFooter={setShowDownloadQuoteFooter}
      />
    );
  }

  /**
   * `userDetails` (content-type-based, flat-join mode). The section's children
   * are flat-named fields already collected into the engine's leaf state
   * (vulcanContentTypeMap `collectChildFields`), so UserDetailsForm reads/writes
   * them through a `formFields` VIEW + a `setFormFields` bridge onto the engine's
   * one state bag. The heavy component is reused UNCHANGED — its own populate /
   * same-as-billing / address-autocomplete logic runs and every write it makes
   * (own or cross-field) is persisted via `form.setValue`.
   */
  function UserDetailsSection({ item, form }) {
    // Referentially STABLE view — recomputed only when engine state changes, so
    // section components whose effects depend on `formFields` don't re-fire every
    // render (matching the app's stable formFields object).
    const { formFields, setFormFields } = useFormFieldsBridge(form);
    // LIVE, so UserDetailsForm's `useEffect(…, [isEditing, orderData])`
    // re-population still fires when a save returns fresh order data.
    const orderData = useLiveOrderData();
    // Fields the USER edited — a ref, since the edit is always followed by a
    // `setFormFields` write that re-renders this section with the new set.
    const editedKeys = React.useRef(new Set());
    const onFieldEdit = React.useCallback(fieldName => {
      editedKeys.current.add(fieldName);
    }, []);
    // UserDetailsForm renders every child, so drop the ones the engine hides via
    // appearsAfterField (e.g. Expiration Date until Permit Number has a value).
    const data = React.useMemo(
      () => ({
        ...item.raw,
        formStepFields: (item.raw.formStepFields || []).filter(
          ({ fields: { name } }) =>
            !formFields[name] || formFields[name].isVisible !== false
        )
      }),
      [item.raw, formFields]
    );
    return (
      <UserDetails
        data={data}
        formFields={formFields}
        setFormFields={setFormFields}
        formErrors={buildFormErrorsView(form, editedKeys.current)}
        onFieldEdit={onFieldEdit}
        orderData={orderData}
        isEditing={isEditing}
        isFormTouched={!!form.isFormTouched}
        setIsFormTouched={setIsFormTouched || (() => {})}
        disabled={false}
        isSameAsCheckboxChecked={false}
        isFieldReset={isFieldReset}
      />
    );
  }

  /**
   * `equipmentGroupSection` (content-type-based, sub-bag mode). Unlike userDetails
   * (flat-join), equipment stores GENUINELY nested state — several package-scoped
   * blobs `values['${package}additionalEquipmentVulcan'][productKey] = {value,…}`
   * that it reads/writes with `getObjectValue`/`setObjectValue` (`_.get/_.set` on
   * `.value`). Those blobs live as ancillary values in the one bag; the same
   * `buildFormFieldsView`/`makeSetFormFields` bridges handle them (deep-clone in,
   * whole-object diff out). `renderTempSensor` is local UI state (the temp-sensor
   * note toggle) that stayed component-local in formStepVulcan, so it stays local
   * here. Pricing derivations (equipmentTotalPrice/dueNow/hardwareDiscount) were
   * passed by formStepVulcan but are UNUSED by the component — wired via the
   * pricing adapter/footer at the step-integration point, not here.
   */
  function EquipmentSection({ item, form }) {
    const [renderTempSensor, setRenderTempSensor] = React.useState(false);
    // Stable view (see UserDetailsSection) — equipment has a `useEffect(…,
    // [formFields])`, so a fresh view every render would loop it.
    const { formFields, setFormFields } = useFormFieldsBridge(form);

    // Seed/normalize the section's nested blob once per section identity —
    // ALWAYS, not just when `form.values[name]` is `undefined`. Without
    // this, "Included Equipment"/"Additional Equipment" headings render with
    // nothing under them (the engine's OWN initial-value builder never walks
    // a bare `custom` item, so `form.values[name]` stays `undefined` for a
    // brand-new session — `EquipmentSectionForm`'s item lists are each gated
    // on `formFields[name] && …`).
    //
    // A SAVED order is NOT safe to treat as "already correctly seeded" just
    // because `form.values[name]` is defined: the forms-core engine's
    // `initialValues` auto-population copies `orderData[name]` VERBATIM, and
    // a saved order stores this blob in the flat SUBMIT shape
    // (`{ productKey: rawNumber }`, from `getEquipmentGroupFormData`) — NOT
    // the rich FORM shape `EquipmentSectionForm` needs
    // (`{ productKey: { value, isVisible, dropdownOptions, … } }`).
    // `normalizeEquipmentGroupSection` (inside `buildInitialEquipmentBlob`)
    // is what reshapes `orderData[name][productKey]` into that rich shape —
    // skipping it for a saved order left the RAW number sitting where a rich
    // object was expected. Confirmed real-data crash: "[equipmentGroupSection]
    // Cannot create property 'isVisible' on number '1'".
    //
    // So this always (re)normalizes on the first time THIS section identity
    // is seen — tracked by name, not a plain boolean, so switching between
    // plan-specific equipment sections (only one of which is declaratively
    // visible at a time, but both can occupy the same tree position) seeds
    // each one once rather than skipping every section after the first.
    //
    // NOTE: this used to re-run on every save, because changing `orderData`
    // rebuilt the registry and remounted this section (resetting the ref).
    // That remount is now gone, so this seeds once per section identity — its
    // stated intent, and it avoids redundantly re-normalizing a blob the
    // engine already holds in the rich shape. Edit mode can't race it:
    // formStepVulcan.js gates mounting on `orderData.id` being present.
    const name = item.raw && item.raw.name;
    const orderData = useLiveOrderData();
    const seededNameRef = React.useRef(null);
    React.useEffect(() => {
      if (!name || seededNameRef.current === name) return;
      seededNameRef.current = name;
      const blob = buildInitialEquipmentBlob(item.raw, {
        isEditing,
        orderData
      });
      if (blob && Object.keys(blob).length) form.setValue(name, blob);
      // Intentionally NOT depending on `form` (a new object every render) —
      // this must run once per section identity; `form.setValue` is read
      // fresh via closure, not tracked as a dependency.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name]);

    // The `useEffect` above only persists the rich blob to `form.values` AFTER
    // the first render. On a refresh / return-to-first-step with a SAVED order,
    // the engine's `initialValues` copies `orderData[name]` verbatim in the
    // flat SUBMIT shape (`{ productKey: rawNumber }`), so the very first render
    // would hand `EquipmentSectionForm` raw numbers where it expects rich
    // objects and it throws while mutating `formFields[name][field].isVisible`
    // ("Cannot create property 'isVisible' on number '1'") before the effect
    // ever runs. Seed synchronously here so the first render already has the
    // rich shape; the effect still runs to persist it for other consumers.
    const seededFormFields = React.useMemo(() => {
      if (!name) return formFields;
      const existing = formFields[name];
      const isRichShape =
        existing &&
        typeof existing === 'object' &&
        Object.values(existing).every(
          entry => entry && typeof entry === 'object'
        );
      if (isRichShape) return formFields;
      const blob = buildInitialEquipmentBlob(item.raw, {
        isEditing,
        orderData
      });
      if (!blob || !Object.keys(blob).length) return formFields;
      return { ...formFields, [name]: blob };
    }, [formFields, name, item.raw, orderData]);

    return (
      <Equipment
        data={item.raw}
        formFields={seededFormFields}
        setFormFields={setFormFields}
        formErrors={buildFormErrorsView(form)}
        isFormTouched={!!form.isFormTouched}
        setIsFormTouched={setIsFormTouched || (() => {})}
        renderTempSensor={renderTempSensor}
        setRenderTempSensor={setRenderTempSensor}
        isExpanded
        setShowDownloadQuoteFooter={setShowDownloadQuoteFooter || (() => {})}
        channelName={channelName}
        // Derived from LIVE engine values so postal-code edits are reflected,
        // matching how formStepVulcan recomputed getPostalCodeFromFormFields on
        // every render. `postalCodeMatches` from appCtx remains an override.
        postalCodeMatches={
          postalCodeMatches || doesPostalCodeMatch(form.values)
        }
      />
    );
  }

  /**
   * `repeatedForm` (content-type-based, sub-bag mode like equipment). RepeatedForm
   * stores an ARRAY of form instances under one key (`values[data.key]`), which it
   * reads/writes via the same rich `formFields`/`setFormFields` contract. The
   * memoized view + bridge carry it (arrays pass through the blob branch). No
   * `[formFields]` effect here, so it's render-stable.
   */
  function RepeatedFormSection({ item, form }) {
    const { formFields, setFormFields } = useFormFieldsBridge(form);
    // LIVE, so RepeatedForm's `useEffect(…, [isEditing, orderData])` re-sync
    // still fires when a save returns fresh order data.
    const orderData = useLiveOrderData();
    return (
      <Repeated
        data={item.raw}
        formFields={formFields}
        setFormFields={setFormFields}
        formErrors={buildFormErrorsView(form)}
        orderData={orderData}
        isEditing={isEditing}
        isFormTouched={!!form.isFormTouched}
        setIsFormTouched={setIsFormTouched || (() => {})}
      />
    );
  }

  /**
   * Gate a content-type section (`userDetails`/`equipment`/`repeatedForm`) on
   * its OWN declarative visibility (appearsAfterField/Value/backendFieldValidation
   * on the Contentful entry itself) — parity of formStepVulcan's generic
   * `isRelatedFieldExisted` check, applied to EVERY content type BEFORE the
   * id/kind branching. The engine's `custom` kind has no built-in equivalent
   * (unlike `field`/`section`, which already get this), so without this gate a
   * section whose own condition isn't met always renders regardless.
   *
   * It ALSO applies Vulcan's IMPERATIVE section hides (`isVulcanSectionHidden`),
   * which no declarative gate covers.
   *
   * The non-live visibility inputs (orderData/isFormTouched/multiSelectFieldNames/
   * backendFieldValidationMode) are read LIVE from `VulcanSectionChromeContext`
   * — the same `chrome.visibilityContext` the collapsible `Section` already uses
   * — rather than captured at factory time. That keeps this registry
   * referentially STABLE: depending on `isFormTouched` here would rebuild the
   * registry on every touch, remounting every section and wiping their local
   * state (see the memo comment in VulcanFormStepRunner.js). With no chrome
   * provider (e.g. a bare FormRunner in tests), the gate still evaluates against
   * the engine's own values — just without the extra edit-mode context.
   */
  function withDeclarativeVisibility(Component) {
    function VisibilityGated({ item, form, ...rest }) {
      const chrome = React.useContext(VulcanSectionChromeContext) || {};
      const orderData = useLiveOrderData();
      const raw = item && item.raw;
      if (
        isVulcanItemHidden(raw, {
          form,
          orderData,
          salesRepOutlet: chrome.salesRepOutlet
        })
      ) {
        return null;
      }
      if (!deriveDeclarativeVisibility(raw, form, chrome.visibilityContext)) {
        return null;
      }
      return <Component item={item} form={form} {...rest} />;
    }
    return VisibilityGated;
  }

  // Per-section error boundaries: a throw in one section degrades to an inline
  // notice for THAT section so the rest of the step still renders (preview
  // resilience). Transparent when nothing throws.
  return withSectionBoundaries({
    CouponBasedDiscount: CouponSection,
    OptikTvTable: OptikTvSection,
    AddOfficePhoneLine: OfficePhoneSection,
    FibreConsolidation: FibreConsolidationSection,
    CopperConsolidation: CopperConsolidationSection,
    TSBConsolidation: TsbConsolidationSection,
    userDetails: withDeclarativeVisibility(UserDetailsSection),
    equipmentGroupSection: withDeclarativeVisibility(EquipmentSection),
    repeatedForm: withDeclarativeVisibility(RepeatedFormSection)
  });
}

export { buildFormFieldsView, makeSetFormFields, buildFormErrorsView };

export default createVulcanSectionRegistry;
